import { createClient } from '@supabase/supabase-js';
import type { ComprobanteResumen, MedioPago } from '../types/comprobante';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export interface ProvinciaEclesiastica {
  id: string;
  nombre: string;
  codigo_iso: string;
  activo: boolean;
  created_at: string;
}

export interface Diocesis {
  id: string;
  nombre: string;
  tipo: 'diocesis' | 'prefectura';
  provincia_eclesiastica_id: string;
  activo: boolean;
  created_at: string;
  provincia_eclesiastica?: ProvinciaEclesiastica;
  cantidad_lumisiales?: number;
}

export interface Lumisial {
  id: string;
  nombre: string;
  ciudad: string;
  region?: string;
  estado_region?: string;
  estado_region_iso2?: string;
  ciudad_id?: string;
  provincia_eclesiastica_id: string;
  diocesis_id: string;
  activo: boolean;
  created_at: string;
  provincia_eclesiastica?: ProvinciaEclesiastica;
  diocesis?: Diocesis;
  cantidad_miembros?: number;
}

export interface Miembro {
  id: string;
  rol_id?: string | null;
  user_id?: string | null;
  nombres: string;
  apellidos: string;
  rut_pasaporte?: string;
  fecha_nacimiento?: string;
  direccion?: string;
  comuna?: string;
  ciudad?: string;
  telefono?: string;
  email?: string;
  actividad_profesion?: string;
  habilidad_talento?: string;
  nombre_contacto_emergencia?: string;
  numero_contacto_emergencia?: string;
  fecha_ingreso?: string;
  lumisial_ingreso?: string;
  pais_ingreso?: string;
  provincia_eclesiastica_ingreso_id?: string;
  ciudad_ingreso?: string;
  lumisial_ingreso_id?: string;
  lumisial_actual_id: string;
  lumisial?: Lumisial;
  cargo?: string[];
  misionero_nacional: boolean;
  instructor_local: boolean;
  instructor_pensum?: string[];
  aporte_mensual: number;
  aporte_cuota_iglesia: number;
  modo_pago_cuota_iglesia?: string;
  tipo_miembro: 'adulto' | 'niño';
  observaciones_miembro?: string | null;
  estado_miembro: 'activo' | 'sin_derechos';
  numero_ingreso: number;
  fecha_ultimo_ingreso?: string;
  estado_cambiado_por_id?: string;
  estado_cambiado_fecha?: string;
  motivo_cambio_estado?: string;
  created_at: string;
  updated_at: string;
  // Campos autoregistro v1.20
  foto_url?: string | null;
  foto_compromiso_hasta?: string | null;
  genero?: 'Dama' | 'Caballero' | null;
  estado_ficha: 'sin_completar' | 'prerregistrado' | 'registrado';
  comentario_devolucion_ficha?: string | null;
  invitation_token?: string | null;
  invitation_expires_at?: string | null;
  invitation_used_at?: string | null;
}

export interface InvitationLog {
  readonly id: string;
  readonly miembro_id: string;
  readonly generated_by: string;
  readonly generated_at: string;
  readonly expires_at: string;
  readonly used_at: string | null;
  generador?: Pick<Miembro, 'nombres' | 'apellidos'>;
}

export interface Movimiento {
  id: string;
  fecha: string;
  tipo: 'ingreso' | 'egreso';
  monto: number;
  descripcion?: string;
  lumisial_id: string | null;
  origen_tipo?: 'miembro' | 'externo' | 'actividad' | 'campaña' | 'lumisial' | 'diocesis' | 'transferencia_cuota_iglesia' | 'financiamiento';
  origen_id?: string;
  // La columna `movimientos.nombre_externo` es `text` NULL sin default: el tipo decía
  // `string` a secas y por eso `ventaActividadService` no compilaba al pasarle el
  // `nombre_externo` de un pedido, que sí puede venir nulo.
  nombre_externo?: string | null;
  categoria_aporte?: 'aporte_mensual' | 'cuota_iglesia';
  categoria_egreso?: 'general' | 'liturgico' | 'misional';
  subcategoria_egreso?: string;
  requiere_reembolso: boolean;
  reembolsado: boolean;
  reembolso_a_miembro_id?: string;
  reembolso_a_nombre_externo?: string;
  anulado: boolean;
  motivo_anulacion?: string;
  fecha_anulacion?: string;
  created_at: string;
  // Campos para movimientos a nivel diocesano/nacional y registro externo
  diocesis_id?: string | null;
  provincia_eclesiastica_id?: string | null;
  es_registro_externo?: boolean;
  registrado_por_usuario_id?: string;
  entidad_origen_tipo?: 'lumisial' | 'diocesis';
  entidad_origen_id?: string;
  afecta_balance: boolean;
  donacion_de_miembro_id?: string;
  donacion_de_nombre_externo?: string;
  pendiente_confirmacion_jan?: boolean | null;
  modo_pago_cuota_iglesia?: string | null;
  readonly envio_campana_id?: string | null;
  readonly aporte_campana_id?: string | null;
  readonly solicitud_id?: string | null;
  readonly saldo_inicial_campana?: boolean;
  readonly anulado_por_usuario_id?: string | null;
  /** Lo asigna la recepción de balance (cambio de permisos); faltaba en el tipo. */
  readonly notificacion_balance_id?: string | null;
  medio_pago?: MedioPago | null;
  folio_recibo?: number | null;
  comprobante_id?: string | null;
  comprobante_pendiente_desde?: string | null;
  comprobante_objecion_motivo?: string | null;
  /** Lo fija la base: registrado desde la entrada en vigor o con comprobante alguna vez. Nunca vuelve a falso (H20). */
  comprobante_bajo_exigencia?: boolean;
  monto_original?: number | null;
  reembolso_fecha?: string | null;
  reembolso_medio_pago?: MedioPago | null;
  reembolso_folio_recibo?: number | null;
  reembolso_comprobante_id?: string | null;
  reembolso_comprobante_pendiente_desde?: string | null;
  reembolso_comprobante_objecion_motivo?: string | null;
  reembolso_comprobante_bajo_exigencia?: boolean;
  /** Embebidos por SELECT_MOVIMIENTO_CON_COMPROBANTES; ausentes en consultas que no los piden. */
  comprobante?: ComprobanteResumen | null;
  comprobante_reembolso?: ComprobanteResumen | null;
}

export interface Deuda {
  id: string;
  lumisial_id: string | null;
  diocesis_id: string | null;
  provincia_eclesiastica_id: string | null;
  acreedor: string;
  descripcion?: string | null;
  monto_total: number;
  numero_cuotas: number;
  valor_cuota: number;
  fecha_inicio_credito: string; // ISO date — cuándo se originó el crédito (puede diferir del primer vencimiento)
  mes_primer_vencimiento: string; // ISO date, siempre día 1 del mes de la primera cuota
  dia_vencimiento: number;    // 1..31
  anulado: boolean;
  motivo_anulacion?: string | null;
  fecha_anulacion?: string | null;
  registrado_por_usuario_id?: string | null;
  created_at: string;
  origen_deuda?: 'compra_credito' | 'prestamo' | null;
  comprobante_origen_id?: string | null;
  movimiento_financiamiento_id?: string | null;
  comprobante_origen?: ComprobanteResumen | null;
}

export interface DeudaPago {
  id: string;
  deuda_id: string;
  numero_cuota: number;
  fecha: string;              // ISO date
  monto: number;
  es_historia_previa: boolean;
  movimiento_id?: string | null;
  anulado: boolean;
  created_at: string;
  // Se llena al joinear con movimientos para derivar vigencia (ver deudaCalculations)
  movimiento_anulado?: boolean | null;
}

export type EstadoDeuda = 'activa' | 'saldada' | 'anulada';

export interface DeudaConEstado extends Deuda {
  pagos: DeudaPago[];
  pagado: number;
  saldo: number;
  cuotasPagadas: number;      // cuotas cubiertas por lo pagado (la última incluye el resto)
  cuotasAtrasadas: number;
  estado: EstadoDeuda;
  entidadNombre: string;      // nombre de la entidad dueña (para agrupar subordinadas)
}

export type EstadoEntidad = 'planificacion' | 'en_curso' | 'finalizada' | 'archivada';

export interface Campaña {
  readonly id: string;
  readonly nombre: string;
  readonly descripcion?: string;
  readonly objetivo_recaudacion: number;
  readonly fecha_inicio: string;
  readonly fecha_fin?: string;
  readonly estado: EstadoEntidad;
  readonly monto_recaudado: number;
  readonly lumisial_id: string;
  readonly campana_master_id?: string;
  readonly objetivo_bloqueado: boolean;
  readonly nivel_origen: 'local' | 'diocesano' | 'nacional' | 'global';
  readonly created_at: string;
}

export interface AporteCampaña {
  readonly id: string;
  readonly campaña_id: string;
  readonly miembro_id?: string;
  readonly nombre_externo?: string;
  readonly monto_comprometido: number;
  readonly monto_aportado: number;
  readonly tipo_aporte: 'dinero' | 'objeto';
  readonly descripcion_objeto?: string;
  readonly created_at: string;
  // Campos para registro externo por admin diocesano/nacional
  readonly es_registro_externo?: boolean;
  readonly registrado_por_usuario_id?: string;
  readonly entidad_origen_tipo?: 'lumisial' | 'diocesis';
  readonly entidad_origen_id?: string;
  readonly fecha_registro_externo?: string;
}

export interface CampanaMaster {
  readonly id: string;
  readonly nombre: string;
  readonly descripcion?: string;
  readonly objetivo_global: number;
  readonly modo_distribucion: 'proporcional' | 'uniforme' | 'libre' | 'fija';
  readonly fecha_inicio: string;
  readonly fecha_fin?: string;
  readonly estado: EstadoEntidad;
  readonly permite_editar_objetivo_local: boolean;
  readonly nivel_origen: 'global' | 'nacional' | 'diocesano';
  readonly creado_por_usuario_id?: string;
  readonly provincia_eclesiastica_id?: string;
  readonly diocesis_id?: string;
  readonly created_at: string;
}

export type EstadoEnvioCampana =
  | 'pendiente' | 'observado' | 'confirmado' | 'cerrado_sin_transferencia';

export interface EnvioCampana {
  readonly id: string;
  readonly campana_id: string | null;
  readonly campana_master_id: string | null;
  readonly lumisial_id: string | null;
  readonly origen_diocesis_id: string | null;
  readonly destino_diocesis_id: string | null;
  readonly destino_provincia_eclesiastica_id: string | null;
  readonly monto: number;
  readonly descripcion: string | null;
  readonly fecha_envio: string;
  readonly estado: EstadoEnvioCampana;
  readonly revision: number;
  readonly solicitud_id: string;
  readonly fecha_confirmacion: string | null;
  readonly confirmado_por_usuario_id: string | null;
  readonly creado_por_usuario_id: string | null;
  readonly nota_admin: string | null;
  readonly notificacion_admin_enviada: boolean;
  readonly notificacion_jal_enviada: boolean;
  readonly created_at: string;
  readonly medio_pago: MedioPago | null;
  readonly comprobante_id: string | null;
  /** Embebido por la consulta de envíos que muestra el respaldo (§9). */
  readonly comprobante?: ComprobanteResumen | null;
}

export interface Notificacion {
  id: string;
  usuario_id: string;
  tipo: string;
  titulo: string;
  cuerpo?: string;
  referencia_id?: string;
  referencia_tipo?: string;
  leida: boolean;
  created_at: string;
}

export interface ActividadEconomica {
  id: string;
  nombre: string;
  descripcion?: string;
  utilidad_objetivo?: number;
  fecha_inicio?: string;
  fecha_fin?: string;
  estado: EstadoEntidad;
  total_ingresos: number;
  total_costos: number;
  utilidad_real: number;
  porcentaje_utilidad: number;
  lumisial_id?: string | null;
  diocesis_id?: string | null;
  provincia_eclesiastica_id?: string | null;
  created_at: string;
}

export interface Produccion {
  id: string;
  actividad_id: string;
  producto_id?: string;
  nombre: string;
  numero: number;
  stock_inicial: number;
  fecha_inicio?: string;
  fecha_fin?: string;
  utilidad_objetivo?: number;
  estado: 'activa' | 'finalizada';
  created_at: string;
}

export interface ProductoActividad {
  id: string;
  actividad_id: string;
  nombre_producto: string;
  precio_unitario: number;
  stock_inicial: number;
  stock_actual: number;
  created_at: string;
}

export interface VentaActividad {
  id: string;
  actividad_id: string;
  produccion_id?: string;
  producto_id?: string;
  miembro_id?: string;
  nombre_externo?: string;
  miembro_referido_id?: string;
  cantidad: number;
  monto_total: number;
  forma_entrega: 'retiro' | 'despacho';
  fecha_entrega?: string;
  direccion_despacho?: string;
  rango_horario_retiro_despacho?: string;
  modo_pago: MedioPago;
  fecha_venta: string;
  estado_pago: 'pendiente' | 'pago_parcial' | 'pagado';
  monto_pagado: number;
  fecha_pago_inicial?: string;
  fecha_pago_completo?: string;
  estado_entrega: 'pendiente' | 'entrega_parcial' | 'entregado';
  cantidad_entregada: number;
  fecha_entrega_inicial?: string;
  fecha_entrega_completa?: string;
  notas?: string;
  anulado: boolean;
  motivo_anulacion?: string;
  fecha_anulacion?: string;
  created_at: string;
}

export interface HistorialEstadoPedido {
  id: string;
  pedido_id: string;
  tipo_cambio: 'pago' | 'entrega' | 'creacion';
  estado_anterior?: string;
  estado_nuevo: string;
  monto_agregado?: number;
  cantidad_agregada?: number;
  notas?: string;
  created_by?: string;
  created_at: string;
}

export interface CostoActividad {
  id: string;
  actividad_id: string;
  produccion_id?: string;
  producto_id?: string;
  descripcion: string;
  monto: number;
  fecha: string;
  tipo_costo: 'materia_prima' | 'producto' | 'otro';
  requiere_reembolso: boolean;
  es_donacion: boolean;
  movimiento_id?: string;
  anulado: boolean;
  motivo_anulacion?: string;
  fecha_anulacion?: string;
  created_at: string;
}

export interface HistorialTrasladoMiembro {
  id: string;
  miembro_id: string;
  provincia_eclesiastica_origen_id: string;
  diocesis_origen_id: string;
  lumisial_origen_id: string;
  provincia_eclesiastica_destino_id: string;
  diocesis_destino_id: string;
  lumisial_destino_id: string;
  fecha_traslado: string;
  motivo?: string | null;
  usuario_id: string;
  created_at: string | null;
  provincia_eclesiastica_origen?: ProvinciaEclesiastica;
  diocesis_origen?: Diocesis;
  lumisial_origen?: Lumisial;
  provincia_eclesiastica_destino?: ProvinciaEclesiastica;
  diocesis_destino?: Diocesis;
  lumisial_destino?: Lumisial;
}

// Fila devuelta por la RPC `obtener_historial_traslados_miembro`, con los nombres ya resueltos en
// el servidor (no FKs para que el cliente los vuelva a resolver bajo la RLS de cada tabla
// geográfica — ver B3 en openspec/changes/separar-permisos-ficha-rol-traslado/UAT.md).
export interface HistorialTrasladoMiembroResuelto {
  id: string;
  fecha_traslado: string;
  motivo: string | null;
  provincia_origen_nombre: string;
  diocesis_origen_nombre: string;
  lumisial_origen_nombre: string;
  lumisial_origen_ciudad: string;
  provincia_destino_nombre: string;
  diocesis_destino_nombre: string;
  lumisial_destino_nombre: string;
  lumisial_destino_ciudad: string;
}

export interface HistorialEstadoMiembro {
  id: string;
  miembro_id: string;
  estado_anterior?: string;
  estado_nuevo: string;
  motivo?: string;
  cambiado_por_id?: string;
  numero_ingreso_nuevo: number;
  created_at: string;
}

// Tipos para notificaciones de balance mensual
export type EstadoNotificacionBalance = 'enviado' | 'recibido' | 'observado';
export type TipoEntidadBalance = 'lumisial' | 'diocesis';

export interface NotificacionBalance {
  id: string;
  tipo_entidad: TipoEntidadBalance;
  entidad_id: string;
  periodo_mes: number;
  periodo_anio: number;
  estado: EstadoNotificacionBalance;
  nota?: string;
  nota_observacion?: string;
  enviado_por_usuario_id: string;
  recibido_por_usuario_id?: string;
  fecha_envio: string;
  fecha_actualizacion?: string;
  created_at: string;
  comprobante_transferencia_id?: string | null;
  comprobante_transferencia?: ComprobanteResumen | null;
}
